export function formatMoney(n) {
  return `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

export function formatShortDate(iso) {
  if (!iso) return '';
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

/** Fill every calendar day between from/to with revenue/orders (0 when missing). */
export function fillDailyRange(daily, from, to) {
  if (!from || !to) return [];
  const map = new Map((daily || []).map((d) => [d.date, d]));
  const out = [];
  const start = new Date(`${from}T12:00:00`);
  const end = new Date(`${to}T12:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [];
  const cursor = new Date(start);
  while (cursor <= end) {
    const key = cursor.toISOString().slice(0, 10);
    const row = map.get(key);
    out.push({
      date: key,
      revenue: row?.revenue ?? 0,
      orders: row?.orders ?? 0,
    });
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
}

/** @param {'revenue'|'orders'} metric */
export function summarizeDailySeries(filled, metric = 'revenue') {
  if (!filled?.length) return null;
  const useOrders = metric === 'orders';
  let peak = null;
  let activeDays = 0;
  let total = 0;
  for (const d of filled) {
    const v = useOrders ? d.orders : d.revenue;
    total += v;
    if (v > 0) activeDays += 1;
    const peakVal = peak ? (useOrders ? peak.orders : peak.revenue) : -1;
    if (v > peakVal) peak = d;
  }
  const peakValue = peak ? (useOrders ? peak.orders : peak.revenue) : 0;
  if (peakValue <= 0) {
    return { activeDays: 0, total: 0, avg: 0, peak: null, quiet: true };
  }
  return {
    activeDays,
    total,
    avg: activeDays > 0 ? total / activeDays : 0,
    peak: { date: peak.date, revenue: peak.revenue, orders: peak.orders, value: peakValue },
    quiet: false,
  };
}

/** @returns {{ text: string, tone: 'up' | 'down' | 'flat' | 'muted' }} */
export function formatDelta(pct) {
  if (pct === null || pct === undefined || Number.isNaN(Number(pct))) {
    return { text: '—', tone: 'muted' };
  }
  const n = Number(pct);
  if (n === 0) return { text: '0%', tone: 'flat' };
  const sign = n > 0 ? '+' : '';
  return { text: `${sign}${n}%`, tone: n > 0 ? 'up' : 'down' };
}

export function paymentMethodLabel(method) {
  const m = String(method || '').toLowerCase();
  if (m === 'razorpay' || m === 'online') return 'Online (Razorpay)';
  if (m === 'offline' || m === 'cod' || m === 'manual') return 'Offline / manual';
  if (!m) return 'Unknown';
  return m.charAt(0).toUpperCase() + m.slice(1);
}

export function downloadAnalyticsCsv(data, rangeLabel) {
  if (!data) return;
  const lines = [];
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  lines.push(['Saloni Designs — shop analytics', rangeLabel || ''].map(esc).join(','));
  lines.push('');
  const period = data.period || {};
  const cmp = data.comparison || {};
  lines.push(['Metric', 'Current period', 'Previous period', 'Change %'].map(esc).join(','));
  lines.push(
    ['Revenue', period.revenue, cmp.previous?.revenue, cmp.revenue_pct].map(esc).join(',')
  );
  lines.push(['Orders', period.orders, cmp.previous?.orders, cmp.orders_pct].map(esc).join(','));
  lines.push(['AOV', period.aov, cmp.previous?.aov, cmp.aov_pct].map(esc).join(','));
  const ff = data.fulfillment || {};
  if (ff && Object.keys(ff).length) {
    lines.push('');
    lines.push(['Fulfillment (open orders snapshot)', 'Count'].map(esc).join(','));
    lines.push(['Paid — not shipped', ff.paid ?? 0].map(esc).join(','));
    lines.push(['Processing', ff.processing ?? 0].map(esc).join(','));
    lines.push(['Partially shipped', ff.partially_shipped ?? 0].map(esc).join(','));
    lines.push(['Fully shipped', ff.shipped ?? 0].map(esc).join(','));
    lines.push(['Completed', ff.completed ?? 0].map(esc).join(','));
  }
  const sh = data.shipments || {};
  if (sh.enabled) {
    lines.push('');
    lines.push(['Partial shipments (selected period)', 'Value'].map(esc).join(','));
    lines.push(['Parcels dispatched', sh.parcels_in_period ?? 0].map(esc).join(','));
    lines.push(['Units shipped in parcels', sh.units_shipped_in_period ?? 0].map(esc).join(','));
    lines.push(['Orders awaiting more parcels', sh.orders_partial_open ?? 0].map(esc).join(','));
  }
  const si = data.shipping_insights || {};
  if (si && Object.keys(si).length) {
    lines.push('');
    lines.push(['Shipping (selected period)', 'Value'].map(esc).join(','));
    lines.push(['Delivery charges', si.shipping_revenue ?? 0].map(esc).join(','));
    lines.push(['Free delivery orders', si.free_shipping_orders ?? 0].map(esc).join(','));
    lines.push(['Avg days paid to ship', si.avg_days_paid_to_ship ?? ''].map(esc).join(','));
    lines.push(['Fulfillment backlog', si.fulfillment_backlog ?? 0].map(esc).join(','));
  }
  const pa = data.period_activity || {};
  if (pa.orders_placed != null) {
    lines.push('');
    lines.push(['Checkout (selected period)', 'Value'].map(esc).join(','));
    lines.push(['Orders placed', pa.orders_placed ?? 0].map(esc).join(','));
    lines.push(['Orders paid', pa.orders_paid ?? 0].map(esc).join(','));
    lines.push(['Conversion %', pa.checkout_conversion_pct ?? ''].map(esc).join(','));
  }
  lines.push('');

  if (Array.isArray(data.daily) && data.daily.length) {
    lines.push(['Date', 'Revenue', 'Orders'].map(esc).join(','));
    data.daily.forEach((d) => {
      lines.push([d.date, d.revenue, d.orders].map(esc).join(','));
    });
    lines.push('');
  }

  const table = (title, headers, rows) => {
    lines.push([title].map(esc).join(','));
    lines.push(headers.map(esc).join(','));
    rows.forEach((row) => lines.push(row.map(esc).join(',')));
    lines.push('');
  };

  if (data.top_products?.length) {
    table(
      'Top products',
      ['Product', 'Category', 'Qty', 'Revenue'],
      data.top_products.map((p) => [p.product_name, p.category_slug, p.quantity, p.revenue])
    );
  }
  if (data.top_categories?.length) {
    table(
      'Top categories',
      ['Category', 'Qty', 'Revenue'],
      data.top_categories.map((c) => [c.category_slug, c.quantity, c.revenue])
    );
  }
  if (data.top_cities?.length) {
    table(
      'Top cities',
      ['City', 'Orders', 'Revenue'],
      data.top_cities.map((c) => [c.city, c.orders, c.revenue])
    );
  }
  if (data.payment_methods?.length) {
    table(
      'Payment mix (paid orders in period)',
      ['Method', 'Orders', 'Revenue'],
      data.payment_methods.map((m) => [paymentMethodLabel(m.method), m.orders, m.revenue])
    );
  }
  const op = data.online_payments || {};
  if (op && Object.keys(op).length) {
    lines.push(['Razorpay / online payments', 'Value'].map(esc).join(','));
    lines.push(['Awaiting Razorpay payment (open)', op.pending_count ?? 0].map(esc).join(','));
    lines.push(['Awaiting payment value INR', op.pending_value ?? 0].map(esc).join(','));
    lines.push(['Paid online in period (payment_method=online)', op.period_paid_online_orders ?? 0].map(esc).join(','));
    lines.push(['Paid online revenue INR', op.period_paid_online_revenue ?? 0].map(esc).join(','));
    lines.push(['Paid with Razorpay payment id in period', op.period_paid_razorpay_orders ?? 0].map(esc).join(','));
    lines.push(['Razorpay paid revenue INR', op.period_paid_razorpay_revenue ?? 0].map(esc).join(','));
    lines.push('');
  }

  const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `saloni-analytics-${(data.range?.from || 'export').replace(/-/g, '')}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
